import api from './api';

/**
 * Clima: condiciones, pronóstico y ciclones.
 *
 * Todo sale del backend y no de los servicios originales, porque ninguno
 * manda cabeceras CORS y porque así el caché se comparte entre todas las
 * pantallas abiertas. Ver el comentario del módulo en
 * apps/core/src/modules/weather/routes.ts.
 */

export interface Ciclon {
  id: string;
  nombre: string;
  /** HU = huracán, TS = tormenta tropical, TD = depresión. */
  clasificacion: string;
  vientoNudos: number;
  presionMb: number | null;
  lat: number;
  lng: number;
  rumboGrados: number | null;
  avanceNudos: number | null;
  actualizado: string;
  /** Anillos del cono, en orden [lng, lat] como GeoJSON. */
  cono: Array<Array<[number, number]>>;
  trayectoria: Array<Array<[number, number]>>;
}

export interface ClimaActual {
  current?: {
    time: string;
    temperature_2m?: number;
    relative_humidity_2m?: number;
    apparent_temperature?: number;
    precipitation?: number;
    weather_code?: number;
    wind_speed_10m?: number;
    wind_direction_10m?: number;
    wind_gusts_10m?: number;
  };
  current_units?: Record<string, string>;
  hourly?: Record<string, Array<number | string>>;
  daily?: Record<string, Array<number | string>>;
  timezone?: string;
}

export interface UbicacionClima {
  lat: number;
  lng: number;
  nombre: string;
}

export async function getCiclones(): Promise<Ciclon[]> {
  const { data } = await api.get('/weather/storms');
  return Array.isArray(data) ? data : [];
}

export async function getClima(lat: number, lng: number): Promise<ClimaActual> {
  const { data } = await api.get('/weather/current', { params: { lat, lng } });
  return data;
}

export async function getUbicacionClima(): Promise<UbicacionClima | null> {
  const { data } = await api.get('/weather/location');
  return data ?? null;
}

export async function setUbicacionClima(u: UbicacionClima): Promise<UbicacionClima> {
  const { data } = await api.put('/weather/location', u);
  return data;
}

/**
 * Traduce el código de condición de Open-Meteo a algo legible.
 *
 * La tabla es la WMO 4677, que es un estándar y no un invento del
 * proveedor: si algún día se cambia de fuente, estos números siguen
 * significando lo mismo. Se agrupan los códigos vecinos —hay tres
 * intensidades de llovizna, por ejemplo— porque para decidir si sales al
 * campo la diferencia entre "llovizna ligera" y "llovizna moderada" no
 * cambia nada.
 */
export function condicionDe(codigo: number | undefined): { clave: string; icono: string } {
  if (codigo === undefined) return { clave: 'unknown', icono: '—' };
  if (codigo === 0) return { clave: 'clear', icono: '☀️' };
  if (codigo <= 2) return { clave: 'partlyCloudy', icono: '🌤️' };
  if (codigo === 3) return { clave: 'cloudy', icono: '☁️' };
  if (codigo <= 48) return { clave: 'fog', icono: '🌫️' };
  if (codigo <= 57) return { clave: 'drizzle', icono: '🌦️' };
  if (codigo <= 67) return { clave: 'rain', icono: '🌧️' };
  if (codigo <= 77) return { clave: 'snow', icono: '🌨️' };
  if (codigo <= 82) return { clave: 'showers', icono: '🌧️' };
  if (codigo <= 86) return { clave: 'snowShowers', icono: '🌨️' };
  return { clave: 'thunderstorm', icono: '⛈️' };
}

/** Los 16 rumbos de la rosa, a partir de los grados de donde VIENE el viento. */
export function rumboDe(grados: number | undefined): string {
  if (grados === undefined || !Number.isFinite(grados)) return '—';
  const rosa = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
  return rosa[Math.round((grados % 360) / 22.5) % 16]!;
}
